import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ShieldCheck, ShieldX, ShieldAlert, Download, RefreshCw, RotateCw, Search, Loader2, Copy, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import { cur } from '@/lib/currency';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useBranchStore } from '@/stores';

const STATUS_BADGE = {
  REPORTED: 'bg-emerald-100 text-emerald-700',
  CLEARED: 'bg-emerald-100 text-emerald-700',
  FAILED: 'bg-rose-100 text-rose-700',
  PENDING: 'bg-amber-100 text-amber-700',
  HELD: 'bg-violet-100 text-violet-700',
};

const fmtDate = (d) => { try { return d ? format(new Date(d), 'MMM dd, yyyy · hh:mm a') : '-'; } catch { return '-'; } };

export default function ZatcaReportingLogPage() {
  const [data, setData] = useState({ rows: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchId, setBranchId] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [retrying, setRetrying] = useState('');
  const [bulkRetrying, setBulkRetrying] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 100;
  const { branches } = useBranchStore();

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, offset: page * pageSize };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (branchId !== 'all') params.branch_id = branchId;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await api.get('/zatca-p2/reporting-log', { params });
      setData(res.data || { rows: [], counts: {}, total: 0 });
    } catch { setData({ rows: [], counts: {}, total: 0 }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter, branchId, startDate, endDate, page]);
  // Reset to first page whenever a filter changes.
  const resetPage = () => setPage(0);

  const retry = async (orderId) => {
    setRetrying(orderId);
    try {
      const res = await api.post(`/zatca-p2/report-order/${orderId}`, {});
      const st = res.data?.zatca?.status;
      if (st === 'REPORTED' || st === 'CLEARED') toast.success(`Invoice ${st.toLowerCase()} to ZATCA`);
      else toast.warning(res.data?.message || 'Retry submitted');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
    finally { setRetrying(''); }
  };

  const filterParams = () => {
    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    if (branchId !== 'all') params.branch_id = branchId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return params;
  };

  const retryAllFailed = async () => {
    setBulkRetrying(true);
    const startFailed = data.counts?.FAILED || 0;
    try {
      const res = await api.post('/zatca-p2/retry-failed', {});
      const n = res.data?.queued || 0;
      if (!n) { toast.info('No failed invoices to retry'); return; }
      toast.success(`Retrying ${n} failed invoice${n === 1 ? '' : 's'}…`);
      setBulkProgress({ total: n, done: 0, finished: false });
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        let curFailed = startFailed;
        try {
          const r = await api.get('/zatca-p2/reporting-log', { params: { ...filterParams(), limit: 1, offset: 0 } });
          curFailed = r.data?.counts?.FAILED ?? startFailed;
        } catch { /* keep polling */ }
        const done = Math.min(n, Math.max(0, startFailed - curFailed));
        const finished = curFailed === 0 || attempts >= 12;
        setBulkProgress({ total: n, done, finished });
        if (finished) {
          await load();
          toast.success(`${done} of ${n} now reported`);
          setTimeout(() => setBulkProgress(null), 6000);
          return;
        }
        setTimeout(poll, 2500);
      };
      setTimeout(poll, 2500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk retry failed'); }
    finally { setBulkRetrying(false); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/zatca-p2/reporting-log/export', { params: filterParams(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zatca_reporting_log_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Exported the full filtered log');
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
    finally { setExporting(false); }
  };

  const openDetail = async (orderId) => {
    setDetailLoading(true);
    setDetail({ order_id: orderId });
    try {
      const res = await api.get(`/zatca-p2/order/${orderId}/zatca`);
      setDetail(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load ZATCA detail');
      setDetail(null);
    } finally { setDetailLoading(false); }
  };

  const rawText = (d) => {
    if (!d) return '';
    const obj = {
      status: d.status, http: d.http, zatca_status: d.zatca_status, mode: d.mode,
      uuid: d.uuid, icv: d.icv, submitted_at: d.submitted_at,
      detail: d.detail, validation: d.validation, raw: d.raw,
    };
    return JSON.stringify(obj, null, 2);
  };

  // Official ZATCA/Fatoora guidance. There is no per-code deep link, so we
  // route by the rule-code family to the most relevant official document.
  const ZATCA_XML_STANDARD = 'https://zatca.gov.sa/en/E-Invoicing/Introduction/Guidelines/Documents/E-invoicing-Detailed-Technical-Guideline.pdf';
  const ZATCA_GUIDELINES = 'https://zatca.gov.sa/en/E-Invoicing/Introduction/Guidelines/Pages/default.aspx';
  const zatcaRuleUrl = (code) => {
    const c = (code || '').toUpperCase();
    if (c.startsWith('BR-KSA') || c.startsWith('BR-CO') || c.startsWith('BR-')) return ZATCA_XML_STANDARD;
    return ZATCA_GUIDELINES;
  };
  const errorCodes = (d) => {
    const v = d?.validation;
    if (!v) return [];
    const msgs = [...(v.errorMessages || []), ...(v.warningMessages || [])];
    const seen = new Set();
    const out = [];
    for (const m of msgs) {
      const code = m?.code || m?.category;
      if (code && !seen.has(code)) { seen.add(code); out.push({ code, message: m?.message }); }
    }
    return out;
  };

  const c = data.counts || {};
  const kpis = [
    { label: 'Reported', val: (c.REPORTED || 0) + (c.CLEARED || 0), Icon: ShieldCheck, cls: 'text-emerald-600' },
    { label: 'Failed', val: c.FAILED || 0, Icon: ShieldX, cls: 'text-rose-600' },
    { label: 'Pending', val: c.PENDING || 0, Icon: ShieldAlert, cls: 'text-amber-500' },
    { label: 'Total', val: data.total || 0, Icon: ShieldCheck, cls: 'text-muted-foreground' },
  ];

  return (
    <DashboardLayout module="reports">
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="zatca-reporting-log-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold font-outfit flex items-center gap-2"><ShieldCheck className="text-emerald-600" /> ZATCA Reporting Log</h1>
            <p className="text-sm text-muted-foreground">Every POS invoice submitted to ZATCA, with the status code and any errors returned.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); resetPage(); }} className="h-9 rounded-md border px-2 text-sm bg-background" data-testid="zatca-log-start" />
            <span className="text-muted-foreground text-xs">to</span>
            <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); resetPage(); }} className="h-9 rounded-md border px-2 text-sm bg-background" data-testid="zatca-log-end" />
            <Select value={branchId} onValueChange={(v) => { setBranchId(v); resetPage(); }}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="zatca-log-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All branches</SelectItem>
                {(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); resetPage(); }}>
              <SelectTrigger className="h-9 w-[140px]" data-testid="zatca-log-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="REPORTED">Reported</SelectItem>
                <SelectItem value="CLEARED">Cleared</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
              </SelectContent>
            </Select>
            {(data.counts?.FAILED || 0) > 0 && (
              <Button variant="destructive" size="sm" onClick={retryAllFailed} disabled={bulkRetrying} data-testid="zatca-log-bulk-retry">
                <RotateCw size={14} className={`mr-1.5 ${bulkRetrying ? 'animate-spin' : ''}`} />Retry all failed ({data.counts.FAILED})
              </Button>
            )}
            {bulkProgress && (
              <span className="text-xs font-medium text-emerald-700 whitespace-nowrap" data-testid="zatca-log-bulk-progress">
                {bulkProgress.done} of {bulkProgress.total} reported{bulkProgress.finished ? '' : '…'}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={load} data-testid="zatca-log-refresh"><RefreshCw size={14} /></Button>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting} data-testid="zatca-log-export">
              {exporting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}{exporting ? 'Exporting…' : 'CSV'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpis.map(k => (
            <Card key={k.label}><CardContent className="p-4 flex items-center gap-3">
              <k.Icon className={k.cls} size={22} />
              <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.label}</p><p className="text-xl font-bold" data-testid={`zatca-log-kpi-${k.label.toLowerCase()}`}>{k.val}</p></div>
            </CardContent></Card>
          ))}
        </div>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Submissions</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Invoice #</th>
                    <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Date</th>
                    <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Branch</th>
                    <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Amount</th>
                    <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">HTTP</th>
                    <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Error / Detail</th>
                    <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Loading…</td></tr>
                  ) : data.rows.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No ZATCA submissions found.</td></tr>
                  ) : data.rows.map((r) => (
                    <tr key={r.order_id} className="border-b hover:bg-muted/30" data-testid={`zatca-log-row-${r.order_id}`}>
                      <td className="px-3 py-2 font-mono text-xs font-medium">#{r.invoice_number}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="px-3 py-2 text-xs">{r.branch || '-'}</td>
                      <td className="px-3 py-2 text-right font-medium whitespace-nowrap">{cur()} {Number(r.amount || 0).toFixed(2)}</td>
                      <td className="px-3 py-2"><Badge className={`${STATUS_BADGE[r.status] || 'bg-muted'} text-[10px]`}>{r.status}{r.zatca_status && r.zatca_status !== r.status ? ` · ${r.zatca_status}` : ''}</Badge></td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.http || '-'}</td>
                      <td className="px-3 py-2 text-xs text-rose-600 max-w-[280px] truncate" title={r.detail || ''}>{r.detail || '-'}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {r.status === 'FAILED' && (
                            <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={retrying === r.order_id} onClick={() => retry(r.order_id)} data-testid={`zatca-log-retry-${r.order_id}`}>
                              <RotateCw size={12} className={`mr-1 ${retrying === r.order_id ? 'animate-spin' : ''}`} />Retry
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => openDetail(r.order_id)} data-testid={`zatca-log-view-${r.order_id}`}>
                            <Search size={12} className="mr-1" />View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.total > pageSize && (
              <div className="flex items-center justify-between mt-3 text-xs" data-testid="zatca-log-pager">
                <span className="text-muted-foreground">
                  Showing {data.total === 0 ? 0 : page * pageSize + 1}–{Math.min((page + 1) * pageSize, data.total)} of {data.total}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="zatca-log-prev">Prev</Button>
                  <span className="text-muted-foreground">Page {page + 1} / {Math.max(1, Math.ceil(data.total / pageSize))}</span>
                  <Button variant="outline" size="sm" disabled={(page + 1) * pageSize >= data.total || loading} onClick={() => setPage((p) => p + 1)} data-testid="zatca-log-next">Next</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl" data-testid="zatca-log-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert size={18} className="text-rose-600" />
              ZATCA submission detail{detail?.invoice_number ? ` · #${detail.invoice_number}` : ''}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-10 text-center text-muted-foreground text-sm"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
          ) : detail ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground text-xs">Status</span><div><Badge className={`${STATUS_BADGE[detail.status] || 'bg-muted'} text-[10px]`}>{detail.status}</Badge></div></div>
                <div><span className="text-muted-foreground text-xs">HTTP</span><div className="font-medium">{detail.http ?? '-'}</div></div>
                <div><span className="text-muted-foreground text-xs">Mode</span><div className="font-medium">{detail.mode || '-'}</div></div>
                <div><span className="text-muted-foreground text-xs">ZATCA status</span><div className="font-medium">{detail.zatca_status || '-'}</div></div>
                <div className="col-span-2"><span className="text-muted-foreground text-xs">Invoice UUID</span><div className="font-mono text-xs break-all">{detail.uuid || '-'}</div></div>
                <div><span className="text-muted-foreground text-xs">ICV</span><div className="font-medium">{detail.icv ?? '-'}</div></div>
                <div><span className="text-muted-foreground text-xs">Submitted</span><div className="text-xs">{fmtDate(detail.submitted_at)}</div></div>
              </div>
              {detail.detail && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Error summary</p>
                  <div className="text-xs text-rose-600 bg-rose-50 rounded-md p-2 whitespace-pre-wrap break-words">{detail.detail}</div>
                </div>
              )}
              {(errorCodes(detail).length > 0 || detail.status === 'FAILED') && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">ZATCA validation rules</p>
                  {errorCodes(detail).length > 0 ? (
                    <div className="flex flex-col gap-1.5">
                      {errorCodes(detail).map((e) => (
                        <a key={e.code} href={zatcaRuleUrl(e.code)} target="_blank" rel="noopener noreferrer"
                          className="flex items-center justify-between gap-2 text-xs rounded-md border px-2.5 py-1.5 hover:bg-amber-50 hover:border-amber-300 transition-colors group"
                          title={e.message || ''} data-testid={`zatca-rule-link-${e.code}`}>
                          <span className="font-mono font-medium text-amber-700">{e.code}</span>
                          <span className="flex items-center gap-1 text-amber-700 opacity-70 group-hover:opacity-100"><ExternalLink size={12} />Open rule</span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <a href={ZATCA_GUIDELINES} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-amber-700 rounded-md border px-2.5 py-1.5 hover:bg-amber-50 hover:border-amber-300 transition-colors"
                      data-testid="zatca-rule-link-general"><ExternalLink size={12} />Open ZATCA validation rules</a>
                  )}
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-muted-foreground">Raw ZATCA response</p>
                  <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => { navigator.clipboard?.writeText(rawText(detail)); toast.success('Copied'); }} data-testid="zatca-log-detail-copy"><Copy size={12} className="mr-1" />Copy</Button>
                </div>
                <pre className="text-[11px] font-mono bg-stone-900 text-stone-100 rounded-md p-3 max-h-72 overflow-auto" data-testid="zatca-log-detail-raw">{rawText(detail)}</pre>
                {detail.status !== 'FAILED' && !detail.detail && !detail.raw && (
                  <p className="text-[11px] text-muted-foreground mt-1">This invoice was accepted — ZATCA only returns detailed validation output on failures.</p>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
